import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";
import { useFetchData } from "@hooks/useFetchData";
import { useMutationData } from "@hooks/useMutationData";
import { apiBase } from "@apis/index";
import queryKeys from "@configs/react-query/query-key";
import useEscapeKey from "@hooks/useEscapeKey";
import UpdateSummaryContent from "@components/Dialogs/UpdateSummaryContent";
import {
  setAwaitingUpdateSummary,
  clearAwaitingUpdateSummary,
} from "@utils/updateSummarySession";

const POLL_INTERVAL_MS = 2000;
const RESTART_FAILURE_THRESHOLD = 3;
const SUMMARY_FETCH_MAX_ATTEMPTS = 30;
const SUMMARY_FETCH_INTERVAL_MS = 2000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const AutoUpdateModal = ({ isOpen, onClose, "data-testid": dataTestId = "auto-update-modal" }) => {
  const fieldContainerTestId = (name) => `${dataTestId}-field-${name}-container`;
  const fieldInputTestId = (name) => `${dataTestId}-field-${name}-input`;
  const fieldValueTestId = (name) => `${dataTestId}-field-${name}-value`;
  const { colors = {} } = useTheme();
  const { t } = useTranslation();

  const translateErrorMessage = (data) => {
    if (data.error_code) {
      const translatedMessage = t(`updater_errors.${data.error_code}`);
      if (translatedMessage !== `updater_errors.${data.error_code}`) {
        return translatedMessage;
      }
    }
    return data.message || data.error || t('update_messages.UNKNOWN_ERROR');
  };

  const translateStatusMessage = (data) => {
    if (data.status_code) {
      const translatedMessage = t(`update_status.${data.status_code}`);
      if (translatedMessage !== `update_status.${data.status_code}`) {
        return translatedMessage;
      }
    }

    if (data.log && data.log.length > 0) {
      return data.log[data.log.length - 1];
    }

    return data.message || t('update_messages.PROCESSING');
  };

  const [updateInfo, setUpdateInfo] = useState({
    status: 'idle',
    message: '',
    progress: 0,
    services: null,
    packageName: '',
  });
  const [updateSummary, setUpdateSummary] = useState(null);
  const [checkTimestamp, setCheckTimestamp] = useState(null);
  const [isRestarting, setIsRestarting] = useState(false);
  const jobIdRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const pollFailuresRef = useRef(0);
  const servicesRef = useRef(null);
  const summaryFetchedRef = useRef(false);
  const lastProgressRef = useRef(0);
  const pollJobStatusRef = useRef(async () => {});

  const {
    data: checkData,
    isLoading: isChecking,
    isError: isCheckError,
    error: checkError,
  } = useFetchData(
    [queryKeys.checkUpdates, checkTimestamp],
    queryKeys.checkUpdates,
    {}, {}, {
    enabled: !!checkTimestamp,
    refetchOnWindowFocus: false,
    staleTime: 0,
    cacheTime: 0
  }
  );

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const { mutate: acknowledgeUpdateSummary } = useMutationData(
    queryKeys.acknowledgeUpdateSummary,
    {
      method: "POST",
      skipErrorToast: true,
    }
  );

  const buildFallbackSummary = useCallback((statusPayload) => ({
    final_status: statusPayload.status,
    timestamp: new Date().toLocaleString(),
    message: translateStatusMessage(statusPayload),
    updated_services: servicesRef.current || {},
  }), [t]);

  const fetchUpdateSummary = useCallback(async (statusPayload) => {
    for (let attempt = 0; attempt < SUMMARY_FETCH_MAX_ATTEMPTS; attempt += 1) {
      try {
        const summary = await apiBase({
          url: queryKeys.getUpdateSummary,
          method: "GET",
          skipErrorToast: true,
        });
        if (summary && typeof summary === "object" && summary.final_status) {
          return summary;
        }
      } catch (error) {
        const status = error?.response?.status;
        if (status && status !== 204 && status >= 500) {
          break;
        }
      }
      await sleep(SUMMARY_FETCH_INTERVAL_MS);
    }
    return buildFallbackSummary(statusPayload);
  }, [buildFallbackSummary]);

  const processStatusPayload = useCallback((data) => {
    if (!data) return;

    if (typeof data.progress === "number") {
      lastProgressRef.current = data.progress;
    }

    if (data.status === 'running') {
      setUpdateInfo(prevState => ({
        ...prevState,
        status: 'in_progress',
        message: translateStatusMessage(data),
        progress: data.progress,
      }));
      return;
    }

    if (data.status === 'success' || data.status === 'failed') {
      if (summaryFetchedRef.current) return;
      summaryFetchedRef.current = true;
      stopPolling();
      setIsRestarting(false);
      setUpdateInfo(prevState => ({
        ...prevState,
        status: 'loading_summary',
        message: translateStatusMessage(data),
        progress: data.progress ?? 100,
      }));

      fetchUpdateSummary(data).then((summary) => {
        clearAwaitingUpdateSummary();
        setUpdateSummary(summary);
        setUpdateInfo(prevState => ({ ...prevState, status: 'complete' }));
      });
    }
  }, [stopPolling, fetchUpdateSummary, t]);

  const pollJobStatus = useCallback(async () => {
    const activeJobId = jobIdRef.current;
    if (!activeJobId || summaryFetchedRef.current) return;

    try {
      const data = await apiBase({
        url: `${queryKeys.updateStatus}/${activeJobId}`,
        method: "GET",
        skipErrorToast: true,
      });
      pollFailuresRef.current = 0;
      setIsRestarting(false);
      processStatusPayload(data);
    } catch {
      pollFailuresRef.current += 1;
      // After images are loaded, updater stops/starts the stack — API outages are expected.
      if (pollFailuresRef.current >= RESTART_FAILURE_THRESHOLD) {
        setIsRestarting(true);
        setUpdateInfo((prevState) => ({
          ...prevState,
          status: 'in_progress',
          message: t("system-update.restarting-system"),
          progress: Math.max(prevState.progress || 0, lastProgressRef.current, 80),
        }));
      }
    }
  }, [processStatusPayload, t]);

  pollJobStatusRef.current = pollJobStatus;

  const startPolling = useCallback(() => {
    stopPolling();
    pollJobStatusRef.current();
    pollingIntervalRef.current = setInterval(() => {
      pollJobStatusRef.current();
    }, POLL_INTERVAL_MS);
  }, [stopPolling]);

  const { mutate: triggerUpdate, isPending: isTriggering } = useMutationData(
    queryKeys.triggerUpdate,
    { method: "POST" },
    {
      onSuccess: (response) => {
        const receivedJobId = response.job_id;
        if (receivedJobId) {
          jobIdRef.current = receivedJobId;
          pollFailuresRef.current = 0;
          summaryFetchedRef.current = false;
          lastProgressRef.current = 0;
          setAwaitingUpdateSummary();
          setIsRestarting(false);
          setUpdateSummary(null);
          setUpdateInfo(prevState => ({ ...prevState, status: 'in_progress', message: t('update_messages.UPDATE_PROCESS_STARTED') }));
          startPolling();
        } else {
          setUpdateInfo({ status: 'error', message: t('updater_errors.UPDATE_JOB_CREATE_FAILED') });
        }
      },
      onError: (error) => {
        const errorMessage = translateErrorMessage(error.response?.data || error);
        setUpdateInfo({ status: 'error', message: errorMessage });
      }
    }
  );

  useEffect(() => {
    if (isCheckError) {
      const errorMessage = translateErrorMessage(checkError.response?.data || checkError);
      setUpdateInfo({ status: 'error', message: errorMessage });
      return;
    }

    if (checkData) {
      const data = checkData;
      // Updater reported an environment problem (e.g. privilege mismatch): show it as an error.
      if (data.error) {
        setUpdateInfo({ status: 'error', message: translateErrorMessage(data) });
        return;
      }
      // Safety net: a service whose current version is 0.0.0 is not visible to the Updater.
      const hasUnknownCurrentVersion = Object.values(data.services_to_update || {})
        .some((versions) => versions?.from === '0.0.0');
      if (data.has_updates && hasUnknownCurrentVersion) {
        setUpdateInfo({ status: 'error', message: t('updater_errors.UPDATER_PRIVILEGE_MISMATCH') });
        return;
      }
      if (data.has_updates) {
        servicesRef.current = data.services_to_update;
        setUpdateInfo({
          status: 'available',
          message: t('update_messages.NEW_UPDATES_AVAILABLE'),
          services: data.services_to_update,
          packageName: data.bundle_file,
          progress: 0
        });
      } else {
        const displayMessage = translateErrorMessage(data);
        setUpdateInfo({ status: 'no_updates', message: displayMessage, progress: 100 });
      }
    }
  }, [checkData, isCheckError, checkError, t]);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const handleSummaryClose = () => {
    clearAwaitingUpdateSummary();
    acknowledgeUpdateSummary(undefined, {
      onSuccess: () => {
        window.location.reload();
      },
    });
  };

  const handleModalClose = () => {
    if (updateInfo.status === 'in_progress' || updateInfo.status === 'loading_summary') {
      return;
    }
    if (updateInfo.status === 'complete') {
      handleSummaryClose();
      return;
    }
    onClose();
  };

  const Button = ({ onClick, children, variant = 'primary', size = 'medium', disabled = false, "data-testid": buttonTestId }) => {
    const baseClasses = 'px-4 py-2 rounded font-medium transition-colors';
    const variantClasses = {
      primary: `${colors.buttonBackgroundGradient} ${colors.buttonBackgroundGradientHover} ${colors.textButton}`,
      success: colors.buttonSuccess,
      outline: `border ${colors.borderPrimary} ${colors.textPrimary} hover:${colors.bgHoverPrimary}`
    };
    const sizeClasses = {
      large: 'px-6 py-3 text-lg',
      small: 'px-3 py-1 text-sm'
    };
    return (
      <button
        onClick={onClick}
        disabled={disabled}
        data-testid={buttonTestId}
        className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size] || ''} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {children}
      </button>
    );
  };

  const Alert = ({ type, children }) => {
    const alertClasses = {
      info: colors.alertInfo,
      success: colors.alertSuccess,
      error: colors.alertError,
    };
    return (
      <div className={`p-4 border rounded-lg ${alertClasses[type]}`}>
        {children}
      </div>
    );
  };

  const Progress = ({ value }) => {
    return (
      <div className={`w-full ${colors.bgSecondary} rounded-full h-2.5`}>
        <div className={`${colors.buttonBackgroundGradient} h-2.5 rounded-full transition-all duration-300`} style={{ width: `${value}%` }}></div>
      </div>
    );
  };

  const isBusy = updateInfo.status === 'in_progress' || updateInfo.status === 'loading_summary';
  useEscapeKey(handleModalClose, isOpen && !isBusy);

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-start lg:justify-center p-4 backdrop-blur-[1px] overflow-x-auto`} data-testid={`${dataTestId}-overlay`}>
      <div className={`border ${colors.borderPrimary} ${colors.bgModal} min-h-[30vh] w-[50rem] min-w-[800px] flex-shrink-0 flex flex-col rounded-lg shadow-xl`} data-testid={dataTestId}>
        <div className={`px-6 py-4 flex-shrink-0 flex justify-between items-start border-b ${colors.borderPrimary}`} data-testid={`${dataTestId}-header`}>
          <div>
            <h2 className={`text-lg font-bold ${colors.textPrimary}`} data-testid={`${dataTestId}-title`}>
              {updateInfo.status === 'complete' ? t("update-summary.title") : t("system-update.title")}
            </h2>
            <p className={`text-sm ${colors.textSecondary} mt-1`}>
              {updateInfo.status === 'complete'
                ? t("update-summary.subtitle")
                : t("system-update.subtitle")}
            </p>
          </div>
          {!isBusy && (
            <button
              data-testid={`${dataTestId}-close-button`}
              onClick={handleModalClose}
              className={`ml-4 p-1 rounded-lg ${colors.textSecondary} hover:${colors.textPrimary} hover:${colors.bgHoverPrimary} transition-colors`}
              aria-label={t("system-update.close")}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-6" data-testid={`${dataTestId}-content`}>
          {updateInfo.status === 'complete' && updateSummary ? (
            <UpdateSummaryContent
              updateSummary={updateSummary}
              showHeader={false}
              data-testid={`${dataTestId}-summary`}
            />
          ) : (
            <div className="space-y-4">
              {updateInfo.status === 'idle' && (
                <div className="text-center" data-testid={fieldContainerTestId("check-for-updates")}>
                  <Button
                    onClick={() => {
                      setCheckTimestamp(Date.now());
                      setUpdateInfo(prevState => ({ ...prevState, status: 'checking', message: t("system-update.checking-for-updates") }));
                    }}
                    disabled={isChecking}
                    size="large"
                    data-testid={fieldInputTestId("check-for-updates")}
                  >
                    {isChecking ? t("system-update.checking") : t("system-update.check-for-updates")}
                  </Button>
                </div>
              )}

              {updateInfo.status === 'checking' && <div className={`${colors.textPrimary}`} data-testid={fieldValueTestId("status-message")}>{t("system-update.checking-for-updates")}</div>}

              {updateInfo.status === 'no_updates' && <Alert type="success"><div data-testid={fieldValueTestId("status-message")}>{updateInfo.message}</div></Alert>}

              {updateInfo.status === 'available' && (
                <div data-testid={fieldContainerTestId("update-available")}>
                  <Alert type="info">
                    <div className="font-semibold" data-testid={fieldValueTestId("status-message")}>{updateInfo.message}</div>
                    <div className="text-sm mt-1" data-testid={fieldValueTestId("package-name")}>{t('update_messages.PACKAGE')}: <strong>{updateInfo.packageName}</strong></div>
                    <p className="mt-2 font-medium">{t('update_messages.SERVICES_TO_BE_UPDATED')}:</p>
                    <ul className="text-sm list-disc list-inside">
                      {updateInfo.services && Object.entries(updateInfo.services).map(([service, versions]) => (
                        <li key={service}>{service}: {versions.from} &rarr; {versions.to}</li>
                      ))}
                    </ul>
                  </Alert>
                  <div className="text-center mt-4">
                    <Button onClick={() => triggerUpdate()} variant="success" size="large" disabled={isTriggering} data-testid={fieldInputTestId("update-now")}>
                      {isTriggering ? t("system-update.triggering") : t("system-update.update-now")}
                    </Button>
                  </div>
                </div>
              )}

              {(updateInfo.status === 'in_progress' || updateInfo.status === 'loading_summary') && (
                <div className="space-y-3" data-testid={fieldContainerTestId("update-progress")}>
                  <div data-testid={fieldValueTestId("progress")}>
                    <Progress value={updateInfo.progress} />
                  </div>
                  <div className={`text-center ${colors.textSecondary} text-sm`} data-testid={fieldValueTestId("status-message")}>
                    {updateInfo.status === 'loading_summary'
                      ? t("system-update.loading-summary")
                      : (updateInfo.message || t('update_messages.UPDATING'))}
                  </div>
                  {isRestarting && updateInfo.status === 'in_progress' && (
                    <Alert type="info">
                      <div data-testid={fieldValueTestId("restarting-message")}>
                        {t("system-update.restarting-system")}
                      </div>
                    </Alert>
                  )}
                </div>
              )}

              {(updateInfo.status === 'error') && (
                <Alert type="error">
                  <div className="font-semibold" data-testid={fieldValueTestId("error-message")}>{updateInfo.message || t("system-update.an-error-occurred")}</div>
                </Alert>
              )}

              {(updateInfo.status === 'no_updates' || updateInfo.status === 'error') && (
                <div className="mt-6 text-center">
                  <Button onClick={() => setUpdateInfo({ status: 'idle', message: '', progress: 0, services: null, packageName: '' })} variant="outline" data-testid={fieldInputTestId("back")}>
                    {t("system-update.back")}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {updateInfo.status === 'complete' && updateSummary && (
          <div className={`px-6 py-4 flex-shrink-0 flex justify-between items-center border-t ${colors.borderPrimary}`} data-testid={`${dataTestId}-footer`}>
            <p className={`text-xs ${colors.textSecondary}`} data-testid={fieldValueTestId("reload-on-close")}>
              {t("system-update.reload-on-close")}
            </p>
            <Button onClick={handleSummaryClose} data-testid={fieldInputTestId("summary-close")}>
              {t("update-summary.close")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AutoUpdateModal;
